import { useCallback, useEffect, useRef, useState } from 'react';
import { setActingEmployeeId } from '../../../services/index/index.service';
import type { EmployeeView } from '../../../services/employees/employees.service';
import type { OrderView } from '../../../services/orders/orders.service';

// One table/order entry the picker fronts; 'table' also covers entering an occupied table.
export type StationWaiterAction =
  | { kind: 'table'; tableId: number; tableLabel: string }
  | { kind: 'order'; order: OrderView }
  | { kind: 'take_away' }
  | { kind: 'kiosk_dine_in' }
  | { kind: 'quick_charge' };

type StationWaiter = { id: number; name: string };

type UseStationWaiterSelectionParams = {
  enabled: boolean;
  onProceed: (action: StationWaiterAction) => void;
};

// Summary: בחירת מלצר בעמדה נייחת — fullscreen per-entry waiter picker; the pick sets the
// x-acting-employee-id attribution header (fire logs + payment lines) until cleared.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-station-waiter-selection
export function useStationWaiterSelection({ enabled, onProceed }: UseStationWaiterSelectionParams) {
  const [pendingAction, setPendingAction] = useState<StationWaiterAction | null>(null);
  const [actingWaiter, setActingWaiterState] = useState<StationWaiter | null>(null);
  // Set right before re-dispatching the intercepted handler so intercept() lets it through once.
  const bypassRef = useRef(false);

  const setActingWaiter = useCallback((waiter: StationWaiter | null) => {
    setActingWaiterState(waiter);
    setActingEmployeeId(waiter?.id ?? null);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setPendingAction(null);
      setActingWaiter(null);
    }
  }, [enabled, setActingWaiter]);

  // Header is module-scoped; drop it when this shell unmounts so no other page inherits it.
  useEffect(() => () => setActingEmployeeId(null), []);

  // Returns true when the action was captured and the picker opened instead.
  const intercept = useCallback((action: StationWaiterAction): boolean => {
    if (!enabled) {
      return false;
    }
    if (bypassRef.current) {
      bypassRef.current = false;
      return false;
    }
    setPendingAction(action);
    return true;
  }, [enabled]);

  const pickWaiter = useCallback((employee: Pick<EmployeeView, 'id' | 'fullName'>) => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) {
      return;
    }
    setActingWaiter({ id: employee.id, name: employee.fullName });
    bypassRef.current = true;
    onProceed(action);
  }, [onProceed, pendingAction, setActingWaiter]);

  const closePicker = useCallback(() => setPendingAction(null), []);
  const clearActingWaiter = useCallback(() => setActingWaiter(null), [setActingWaiter]);

  return {
    isPickerOpen: pendingAction !== null,
    actingWaiter,
    intercept,
    pickWaiter,
    closePicker,
    clearActingWaiter,
  };
}
